import { isPresenceSelection } from './selection';
import type { DocumentScope, Store } from '../state/store';
import { diffDocument, previewDelta, sameJson } from './patch';
import { isPresenceContext, isPresenceViewport } from './presence';
import type { PresenceSource } from './presenceSource';
import { t } from '../i18n/I18n';
import type { CollaborationState, CollaborationTransport, CommandKey, CommitDecision, DocumentOwner, DocumentPreview, Json, LocalProposal,
  PresenceEvent, PresenceInput, Receipt, RecoveryStorage, SharedEvent, SharedSnapshot, SharedStoreAdapter, SubmitReply, SubmitRequest } from './protocol';

export type CollaborationSessionOptions = { store: Store; transport: CollaborationTransport; storage: RecoveryStorage; clientId: string; now?: () => number };
type PendingRecord = { protocol: 1; request: SubmitRequest; candidate: LocalProposal['after'] };
type Pending = { key: string; record: PendingRecord; owner?: DocumentOwner; origin?: DocumentScope };
const code = (error: unknown): string => error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const unref = (timer: ReturnType<typeof setTimeout>): void => { (timer as unknown as { unref?: () => void }).unref?.(); };

export class CollaborationSession implements SharedStoreAdapter, PresenceSource {
  readonly kind = 'session';
  private state: CollaborationState = { status: 'local', snapshot: null, participants: [], previews: [], error: null, pendingCommandId: null };
  private listeners = new Set<() => void>();
  private epoch = 0;
  private origin: DocumentScope | null = null;
  private unsubscribe: (() => void) | null = null;
  private stopPreview: (() => void) | null = null;
  private stopStore: (() => void) | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private publishTimer: ReturnType<typeof setTimeout> | null = null;
  private pending: Pending | null = null;
  private inFlight: Promise<CommitDecision> | null = null;
  private reconnect: Promise<void> | null = null;
  private refresh: Promise<void> | null = null;
  private sequence = 0;
  private packets = new Map<string, { packet: PresenceEvent; receivedAt: number }>();
  private terminalGestures = new Map<string, number>();
  private cursor: Pick<PresenceInput, 'floor' | 'mode' | 'cursor' | 'presence' | 'viewport' | 'selection'> | null = null;
  private preview: PresenceInput['preview'] = null;
  private localPreview: DocumentPreview | null = null;
  private previewDirty = false;
  private previewBaseRevision = 0;
  private settled: PresenceInput['settled'];
  private presenceFrame: number | null = null;
  private latestPacket: PresenceInput | null = null;
  private publishing = 0;
  private urgentPacket = false;
  private lastPublish = -Infinity;
  private wantedRevision = 0;

  constructor(private readonly options: CollaborationSessionOptions) {}
  private now(): number { return (this.options.now ?? Date.now)(); }
  get writable(): boolean { return this.state.status === 'ready' && !this.pending; }
  get clientId(): string { return this.options.clientId; }
  get = (): CollaborationState => this.state;
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };

  async start(snapshot: SharedSnapshot): Promise<void> {
    this.close();
    this.origin = this.options.store.captureDocumentScope();
    if (!this.origin) throw new Error('Open the shared document before starting collaboration.');
    this.options.store.attachShared(this);
    this.wantedRevision = snapshot.revision;
    this.set({ snapshot: clone(snapshot), status: 'connecting', error: null });
    this.options.store.acceptShared(this.state.snapshot!);
    this.stopPreview = this.options.store.subscribeDocumentPreviews(value => this.receiveLocalPreview(value));
    this.stopStore = this.options.store.subscribe(() => {
      const scope = this.options.store.captureDocumentScope();
      if (!scope || scope.projectId !== this.origin?.projectId || scope.epoch !== this.origin?.epoch) this.close();
    });
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    this.heartbeat = setInterval(() => {
      this.expire();
      if (this.state.status === 'offline' || this.state.status === 'reconnecting') void this.retry();
      else if (this.state.status !== 'denied') { this.queuePacket(); void this.catchUp(); }
    }, 5000);
    unref(this.heartbeat);
    await this.connect();
  }

  close(): void {
    // Departure must clear selection even directly after a presence packet. Its higher sequence wins over in-flight packets.
    if (this.state.snapshot && this.cursor && this.active()) {
      const packet: PresenceInput = { protocol: 1, documentId: this.state.snapshot.documentId, generation: this.state.snapshot.generation, clientId: this.options.clientId,
        seq: ++this.sequence, ...this.cursor, cursor: null, presence: null, viewport: null, selection: null, preview: null };
      void this.options.transport.publish(packet).catch(() => {});
    }
    this.epoch++;
    this.unsubscribe?.(); this.unsubscribe = null;
    this.stopPreview?.(); this.stopPreview = null;
    this.stopStore?.(); this.stopStore = null;
    if (this.heartbeat) clearInterval(this.heartbeat);
    if (this.publishTimer) clearTimeout(this.publishTimer);
    this.heartbeat = null; this.publishTimer = null;
    this.publishing = 0; this.urgentPacket = false; this.lastPublish = -Infinity;
    this.terminalGestures.clear();
    this.settled = undefined;
    if (this.presenceFrame !== null) cancelAnimationFrame(this.presenceFrame);
    this.presenceFrame = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.options.store.isSharedAdapter(this)) this.options.store.attachShared(null);
    this.origin = null; this.latestPacket = null; this.packets.clear(); this.preview = null; this.localPreview = null; this.previewDirty = false; this.cursor = null; this.pending = null; this.inFlight = null;
    this.set({ status: 'local', snapshot: null, participants: [], previews: [], error: null, pendingCommandId: null });
  }

  private active(epoch = this.epoch): boolean {
    const scope = this.options.store.captureDocumentScope();
    return epoch === this.epoch && !!this.origin && scope?.projectId === this.origin.projectId && scope.epoch === this.origin.epoch;
  }

  private async connect(): Promise<void> {
    const snapshot = this.state.snapshot;
    if (!snapshot || !this.active()) return;
    const epoch = ++this.epoch;
    this.publishing = 0; this.lastPublish = -Infinity; this.latestPacket = null; this.urgentPacket = false;
    if (this.publishTimer) clearTimeout(this.publishTimer);
    this.publishTimer = null;
    this.unsubscribe?.(); this.unsubscribe = null;
    try {
      const unsubscribe = await this.options.transport.subscribe(snapshot.documentId, snapshot.generation,
        event => { if (this.active(epoch)) this.receive(event); }, status => {
          if (this.active(epoch) && status === 'disconnected' && this.state.status !== 'denied') this.set({ status: 'reconnecting', error: t('#CollabConnectionLost') });
        });
      if (!this.active(epoch)) { unsubscribe(); return; }
      this.unsubscribe = unsubscribe;
      const latest = await this.options.transport.read(snapshot.documentId);
      if (!this.active(epoch)) return;
      // Only a new membership-authorized read can reopen a denied session.
      if (this.state.status === 'denied') this.set({ status: 'reconnecting', error: null });
      this.adopt(latest);
      if (latest.generation !== snapshot.generation) { await this.connect(); return; }
      await this.recover();
      if (this.active(epoch) && !this.pending && this.state.status !== 'conflict' && this.state.status !== 'denied') {
        this.set({ status: 'ready', error: null, pendingCommandId: null }); this.queuePacket();
      }
    } catch (error) {
      if (!this.active(epoch)) return;
      try {
        const latest = await this.options.transport.read(snapshot.documentId);
        if (!this.active(epoch)) return;
        if (latest.generation !== snapshot.generation) { this.adopt(latest); await this.connect(); }
        else this.failed(error);
      } catch (readError) { if (this.active(epoch)) this.failed(readError); }
    }
  }

  private adopt(snapshot: SharedSnapshot, owner?: DocumentOwner): void {
    if (!this.active() || snapshot.documentId !== this.state.snapshot?.documentId || snapshot.revision < this.state.snapshot.revision) return;
    if (this.cursor && this.cursor.floor.layoutVersion !== snapshot.layoutVersion) {
      this.cursor = null; this.latestPacket = null; this.preview = null; this.localPreview = null; this.previewDirty = false;
    }
    this.set({ snapshot: clone(snapshot) });
    this.options.store.acceptShared(this.state.snapshot!, owner);
    this.expire();
  }

  private async catchUp(): Promise<void> {
    if (this.refresh) return this.refresh;
    const snapshot = this.state.snapshot, epoch = this.epoch;
    if (!snapshot || !this.active(epoch)) return;
    const work = (async () => {
      try {
        do {
          const latest = await this.options.transport.read(snapshot.documentId);
          if (!this.active(epoch)) return;
          this.adopt(latest);
          if (latest.generation !== snapshot.generation) { this.packets.clear(); this.expire(); await this.retry(); return; }
        } while (this.active(epoch) && this.state.snapshot!.revision < this.wantedRevision);
      } catch (error) { if (this.active(epoch)) this.failed(error); }
    })();
    this.refresh = work;
    try { await work; } finally { if (this.refresh === work) this.refresh = null; }
  }

  private receive(event: SharedEvent): void {
    if (event.kind === 'presence') { this.receivePresence(event.value); return; }
    if (event.kind === 'access_changed') { this.packets.clear(); this.expire(); void this.retry(); return; }
    if (event.gestureId) this.terminalGestures.set(event.userId + ':' + event.gestureId, event.revision);
    this.wantedRevision = Math.max(this.wantedRevision, event.revision);
    while (this.terminalGestures.size > 256) this.terminalGestures.delete(this.terminalGestures.keys().next().value!);
    this.expire();
    if (event.commandId === this.pending?.record.request.commandId && event.callerId === this.pending.record.request.callerId) return;
    if (event.generation !== this.state.snapshot?.generation) { void this.retry(); return; }
    if (event.revision > this.state.snapshot.revision) void this.catchUp();
  }

  async retry(): Promise<void> {
    if (this.reconnect) return this.reconnect;
    if (!this.state.snapshot || !this.active()) return;
    if (this.state.status !== 'denied') this.set({ status: 'reconnecting', error: this.pending ? t('#CollabCheckingSave') : null });
    const work = this.connect(); this.reconnect = work;
    try { await work; } finally { if (this.reconnect === work) this.reconnect = null; }
  }

  async replay(key: CommandKey, request: Json): Promise<Receipt | null> {
    const snapshot = this.state.snapshot;
    if (!snapshot) return null;
    const receipt = await this.options.transport.receipt(snapshot.documentId, key, request);
    const pending = this.pending;
    if (receipt && pending?.record.request.commandId === key.commandId && pending.record.request.callerId === key.callerId) {
      const current = await this.options.transport.read(snapshot.documentId);
      this.finish(pending, receipt.status === 'accepted' ? { status: 'accepted', receipt, snapshot: current } : { status: 'rejected', code: 'conflict', snapshot: current });
    }
    return receipt;
  }

  async submit(proposal: LocalProposal): Promise<CommitDecision> {
    const snapshot = this.state.snapshot;
    if (!this.writable || !snapshot) return { status: 'rejected', code: this.state.status === 'denied' ? 'forbidden' : 'busy' };
    const scope = this.options.store.captureDocumentScope();
    if (!this.active() || !scope || scope.levelIndex !== proposal.scope.levelIndex || scope.scopeVersion !== proposal.scope.scopeVersion) return { status: 'rejected', code: 'scope_conflict' };
    const diff = proposal.patch ? { ok: true as const, value: proposal.patch } : diffDocument(proposal.before, proposal.after,
      { baseRevision: snapshot.revision, layoutVersion: snapshot.layoutVersion, replace: proposal.kind === 'replace' });
    if (!diff.ok) return { status: 'rejected', code: diff.code };
    if (!diff.value) return { status: 'unchanged', revision: snapshot.revision };
    const request: SubmitRequest = clone({ protocol: 1, documentId: snapshot.documentId, callerId: proposal.owner.callerId ?? this.options.clientId,
      commandId: proposal.owner.commandId ?? crypto.randomUUID(), baseRevision: snapshot.revision, patch: diff.value,
      request: proposal.owner.request ?? JSON.parse(JSON.stringify({ kind: proposal.kind, patch: diff.value })) as Json, ...(proposal.owner.result !== undefined ? { result: proposal.owner.result } : {}),
      ...(proposal.gestureId ? { gestureId: proposal.gestureId } : {}) });
    const key = `hp.collab.pending.${snapshot.self.userId}.${snapshot.documentId}.${this.options.clientId}`;
    const record: PendingRecord = { protocol: 1, request, candidate: clone(proposal.after) };
    try { this.options.storage.setItem(key, JSON.stringify(record)); }
    catch {
      this.set({ status: 'offline', error: t('#CollabErrorWriteChanges') });
      return { status: 'rejected', code: 'invalid' };
    }
    this.pending = { key, record, owner: proposal.owner, origin: this.origin! };
    // Keep the final pose visible to peers while the durable command is in flight.
    // A null preview before acknowledgement would snap them back to the old base.
    if (proposal.gestureId) {
      this.localPreview = { gestureId: proposal.gestureId, slot: proposal.scope.levelIndex,
        baseline: proposal.before.scene.levels[proposal.scope.levelIndex], current: proposal.after.scene.levels[proposal.scope.levelIndex] };
      this.previewBaseRevision = request.baseRevision; this.previewDirty = true;
    }
    this.queuePacket(true);
    this.set({ status: 'saving', error: null, pendingCommandId: request.commandId });
    return this.dispatch(this.pending);
  }

  private async dispatch(pending: Pending): Promise<CommitDecision> {
    const network = this.options.transport.submit(pending.record.request).then(reply => this.finish(pending, reply), error => {
      if (['forbidden', 'invalid', 'duplicate_mismatch'].includes(code(error))) return this.finish(pending, { status: 'rejected', code: code(error) as 'forbidden' | 'invalid' | 'duplicate_mismatch' });
      if (this.active() && this.pending === pending && this.state.status !== 'denied') this.set({ status: 'offline', pendingCommandId: pending.record.request.commandId, error: t('#CollabCheckingSaveOffline') });
      return { status: 'pending' as const, commandId: pending.record.request.commandId };
    });
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<CommitDecision>(resolve => {
      timer = setTimeout(() => {
        if (this.active() && this.pending === pending && this.state.status !== 'denied') this.set({ status: 'offline', error: t('#CollabResponseDelayed') });
        resolve({ status: 'pending', commandId: pending.record.request.commandId });
      }, 15000); unref(timer);
    });
    this.inFlight = network;
    void network.finally(() => { clearTimeout(timer!); if (this.inFlight === network) this.inFlight = null; });
    return Promise.race([network, timeout]);
  }

  private finish(pending: Pending, reply: SubmitReply): CommitDecision {
    const request = pending.record.request;
    const current = this.active() && this.state.snapshot?.documentId === request.documentId && (!pending.origin || pending.origin.epoch === this.origin?.epoch && pending.origin.projectId === this.origin.projectId);
    const ownsPending = this.pending?.record.request.commandId === request.commandId && this.pending.record.request.callerId === request.callerId;
    const decision: CommitDecision = reply.status === 'accepted' ? { status: 'committed', revision: reply.receipt.acceptedRevision! } : { status: 'rejected', code: reply.code };
    if (current) {
      if (reply.snapshot && this.state.status !== 'denied') this.adopt(reply.snapshot.revision < this.state.snapshot!.revision ? this.state.snapshot! : reply.snapshot,
        reply.status === 'accepted' ? pending.owner : undefined);
      this.options.store.resolveSharedCommand(request.commandId, decision);
    }
    try {
      const stored = JSON.parse(this.options.storage.getItem(pending.key) ?? 'null') as PendingRecord | null;
      if (stored && sameJson(stored.request, request)) this.options.storage.removeItem(pending.key);
    } catch { /* a remaining immutable record reconciles through the receipt */ }
    if (ownsPending) this.pending = null;
    if (current && ownsPending) {
      this.localPreview = null; this.previewDirty = false;
      if (reply.status === 'accepted' && request.gestureId) this.settled = { gestureId: request.gestureId, revision: reply.receipt.acceptedRevision! };
      else this.preview = null;
      this.queuePacket(true);
      this.preview = null;
      if (reply.status === 'rejected' && reply.code === 'forbidden') this.failed({ code: 'forbidden' });
      if (this.state.status === 'denied') this.set({ pendingCommandId: null });
      else this.set({ status: reply.status === 'accepted' ? 'ready' : 'conflict', pendingCommandId: null,
        error: reply.status === 'accepted' ? null : t('#CollabErrorConflict') });
    }
    return decision;
  }

  private async recover(): Promise<void> {
    const snapshot = this.state.snapshot, epoch = this.epoch;
    if (!snapshot) return;
    const prefix = `hp.collab.pending.${snapshot.self.userId}.${snapshot.documentId}.`;
    const entries: Pending[] = [];
    for (let index = 0; index < this.options.storage.length; index++) {
      const key = this.options.storage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const record = JSON.parse(this.options.storage.getItem(key) ?? 'null') as PendingRecord;
      if (!record || record.protocol !== 1 || record.request?.protocol !== 1 || record.request.documentId !== snapshot.documentId) throw new Error(t('#CollabErrorPendingUnreadable'));
      entries.push({ key, record });
    }
    for (const entry of entries) {
      if (!this.active(epoch)) return;
      const pending = this.pending?.key === entry.key && sameJson(this.pending.record.request, entry.record.request) ? this.pending : entry;
      this.pending = pending;
      this.set({ status: 'saving', pendingCommandId: pending.record.request.commandId, error: t('#CollabCheckingSave') });
      const request = pending.record.request, receipt = await this.options.transport.receipt(snapshot.documentId, request, request.request);
      if (!this.active(epoch)) return;
      if (receipt) {
        const current = await this.options.transport.read(snapshot.documentId);
        if (!this.active(epoch)) return;
        this.finish(pending, receipt.status === 'conflict' ? { status: 'rejected', code: 'conflict', snapshot: current } : { status: 'accepted', receipt, snapshot: current });
      } else if (this.inFlight) return;
      else if ((await this.dispatch(pending)).status === 'pending') return;
    }
  }

  sendCursor(input: Pick<PresenceInput, 'floor' | 'mode' | 'cursor' | 'presence' | 'viewport' | 'selection'>): void { this.cursor = input; this.queuePacket(); }
  private receiveLocalPreview(value: DocumentPreview | null): void {
    const snapshot = this.state.snapshot;
    if (!snapshot) return;
    if (!value && this.pending?.record.request.gestureId) return;
    if (!value) { this.preview = null; this.localPreview = null; this.previewDirty = false; }
    else {
      if (this.localPreview?.gestureId !== value.gestureId) this.previewBaseRevision = snapshot.revision;
      // Keep the latest immutable gesture state; diff it only when a packet can actually leave.
      this.localPreview = value; this.previewDirty = true;
      if (this.cursor?.floor.slot !== value.slot || this.cursor.floor.layoutVersion !== snapshot.layoutVersion) {
        this.cursor = { floor: { slot: value.slot, layoutVersion: snapshot.layoutVersion }, mode: this.cursor?.mode ?? 'plan', cursor: null, presence: null, viewport: null, selection: null };
      }
    }
    this.queuePacket(!value);
  }
  private queuePacket(urgent = false): void {
    const snapshot = this.state.snapshot;
    if (!snapshot || !this.cursor || !this.active() || ['denied', 'offline', 'local'].includes(this.state.status)) return;
    this.sequence = Math.max(this.sequence + 1, Math.floor(this.now() * 1000));
    this.latestPacket = { protocol: 1, documentId: snapshot.documentId, generation: snapshot.generation, clientId: this.options.clientId,
      seq: this.sequence, ...this.cursor, preview: this.preview, ...(this.settled ? { settled: this.settled } : {}) };
    this.urgentPacket ||= urgent;
    // Bound normal traffic, coalescing the latest sample. One extra slot lets a
    // cancel/final pose overtake a delayed preview; sequence numbers reject it later.
    if (this.urgentPacket && this.publishing < 2) {
      if (this.publishTimer) clearTimeout(this.publishTimer);
      this.publishTimer = null; void this.publishPacket();
    } else if (!this.publishing && !this.publishTimer) {
      this.publishTimer = setTimeout(() => { this.publishTimer = null; void this.publishPacket(); }, Math.max(0, Math.ceil(1000 / 60 - (this.now() - this.lastPublish))));
      unref(this.publishTimer);
    }
  }
  private async publishPacket(): Promise<void> {
    const packet = this.latestPacket, epoch = this.epoch;
    if (!packet || !this.active(epoch) || this.publishing >= (this.urgentPacket ? 2 : 1)) return;
    this.latestPacket = null; this.urgentPacket = false;
    if (this.previewDirty && this.localPreview) {
      const value = this.localPreview, delta = previewDelta(value.baseline, value.current);
      this.preview = delta.ok ? { gestureId: value.gestureId, baseRevision: this.previewBaseRevision, delta: delta.value } : null;
      this.previewDirty = false;
      packet.preview = this.preview;
    }
    if (new TextEncoder().encode(JSON.stringify(packet)).byteLength > 262144) packet.preview = null;
    this.publishing++; this.lastPublish = this.now();
    try {
      const result = await this.options.transport.publish(packet);
      if (this.active(epoch) && result.generation !== this.state.snapshot?.generation) void this.retry();
    } catch (error) { if (this.active(epoch) && code(error) === 'forbidden') this.failed(error); }
    finally { if (this.active(epoch)) { this.publishing--; if (this.latestPacket) this.queuePacket(); } }
  }
  private receivePresence(value: PresenceEvent): void {
    const snapshot = this.state.snapshot;
    if (!snapshot || !value || this.state.status === 'denied' || value.protocol !== 1 || value.documentId !== snapshot.documentId || value.generation !== snapshot.generation
      || !value.floor || !Number.isSafeInteger(value.floor.slot) || value.floor.slot < 0
      || value.floor.layoutVersion !== snapshot.layoutVersion || !snapshot.scene.levels[value.floor.slot]
      || !isPresenceSelection(value.selection)
      || !isPresenceContext(value.presence, value.mode, value.cursor)
      || !isPresenceViewport(value.viewport, value.mode, snapshot.scene.levels[value.floor.slot])
      || value.settled !== undefined && (!value.settled || typeof value.settled.gestureId !== 'string' || !Number.isSafeInteger(value.settled.revision) || value.settled.revision < 1)
      || !Number.isSafeInteger(value.seq) || value.clientId === this.options.clientId && value.userId === snapshot.self.userId) return;
    const key = value.userId + ':' + value.clientId, previous = this.packets.get(key);
    if (previous && previous.packet.seq >= value.seq) return;
    if (value.settled) {
      this.terminalGestures.set(value.userId + ':' + value.settled.gestureId, value.settled.revision);
      while (this.terminalGestures.size > 256) this.terminalGestures.delete(this.terminalGestures.keys().next().value!);
      // A receipt marker may arrive ahead of the command notification. Read once;
      // only authoritative snapshots can change the document revision.
      if (value.settled.revision > snapshot.revision) void this.catchUp();
    }
    let packet = clone(value);
    // A commit notification can precede the snapshot fetch. Keep its last preview
    // until that revision is adopted even if the sender has already cleared it.
    const terminal = previous?.packet.preview && this.terminalGestures.get(value.userId + ':' + previous.packet.preview.gestureId);
    if (!packet.preview && terminal && terminal > snapshot.revision) packet = { ...packet, preview: previous!.packet.preview };
    this.packets.set(key, { packet, receivedAt: this.now() });
    // A burst from many peers produces one presentation update before the next paint.
    if (typeof requestAnimationFrame === 'function') {
      this.presenceFrame ??= requestAnimationFrame(() => { this.presenceFrame = null; this.expire(); });
    } else this.expire();
  }
  private expire(): void {
    const snapshot = this.state.snapshot;
    if (!snapshot) return;
    for (const [key, { packet }] of this.packets) if (packet.generation !== snapshot.generation || packet.floor.layoutVersion !== snapshot.layoutVersion) this.packets.delete(key);
    // Keep sequence high-water marks after display expiry so delayed duplicates cannot revive a cursor.
    const participants = [...this.packets.values()].filter(value => value.receivedAt >= this.now() - 15000).map(value => value.packet);
    const previews = participants.filter(value => value.preview && value.preview.baseRevision >= snapshot.revision
      && (this.terminalGestures.get(value.userId + ':' + value.preview.gestureId) ?? Infinity) > snapshot.revision);
    const samePackets = (a: PresenceEvent[], b: PresenceEvent[]) => a.length === b.length && a.every((value, index) => value === b[index]);
    if (!samePackets(participants, this.state.participants) || !samePackets(previews, this.state.previews)) {
      this.set({ participants, previews }); this.options.store.setRemotePreviews(previews);
    }
  }
  private onVisibility = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') { if (this.cursor) this.sendCursor({ ...this.cursor, cursor: null, presence: null, viewport: null, selection: null }); }
    else { this.expire(); void this.retry(); }
  };
  private failed(error: unknown): void {
    const denied = ['forbidden', '42501'].includes(code(error));
    if (this.state.status === 'denied' && !denied) return;
    if (denied) {
      this.epoch++;
      this.unsubscribe?.(); this.unsubscribe = null;
      this.packets.clear(); this.preview = null; this.localPreview = null; this.previewDirty = false; this.options.store.setRemotePreviews([]);
      this.set({ status: 'denied', error: t('#CollabErrorDenied'), participants: [], previews: [] });
      this.options.store.cancelStroke(); this.options.store.endMaterialTransform(false); this.options.store.clearPropertiesPreview();
      return;
    }
    const message = error instanceof Error ? error.message : String(error);
    this.set({ status: 'offline', error: code(error) === 'unavailable' || message === 'Unable to connect to live collaboration.'
      ? t('#CollabErrorConnect') : message });
  }
  private set(patch: Partial<CollaborationState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) { try { fn(); } catch { /* observers do not own delivery */ } }
  }
}
